import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Stack,
    Alert,
    CircularProgress,
    Chip,
    Button,
    Paper,
    Divider
} from '@mui/material';
import {
    Close,
    PhotoLibrary,
    ZoomIn,
    Business,
    LocalShipping,
    CheckCircle,
    Warning
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api';

const LoadSecuringGallery = ({ searchTerm, filterCategory }) => {
    const { user } = useAuth();
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchImages();
    }, [searchTerm, filterCategory]);

    const fetchImages = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                type: 'image',
                category: 'load_securing',
                ...(searchTerm && { search: searchTerm }),
                ...(filterCategory && filterCategory !== 'load_securing' && { category: filterCategory })
            });

            const response = await apiClient.get(`/safety/content?${params}`);
            setImages(response.data.data || []);
        } catch (error) {
            console.error('Error fetching images:', error);
            setError('Failed to load images');
        } finally {
            setLoading(false);
        }
    };

    const handleImageClick = (image) => {
        setSelectedImage(image);
        setImageDialogOpen(true);
    };

    const getSecurityRating = (tags) => {
        if (tags.includes('excellent') || tags.includes('best_practice')) {
            return { label: 'Excellent', color: 'success', icon: <CheckCircle /> };
        } else if (tags.includes('good') || tags.includes('recommended')) {
            return { label: 'Good', color: 'primary', icon: <CheckCircle /> };
        } else if (tags.includes('poor') || tags.includes('needs_improvement')) {
            return { label: 'Needs Improvement', color: 'warning', icon: <Warning /> };
        }
        return { label: 'Example', color: 'default', icon: null };
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                <PhotoLibrary color="primary" fontSize="large" />
                <Typography variant="h5">
                    Load Securing Examples ({images.length})
                </Typography>
            </Stack>

            <Alert severity="info" sx={{ mb: 3 }}>
                View real examples of properly secured loads from actual customer deliveries. 
                These photos demonstrate best practices for different types of cargo and equipment.
            </Alert>

            {images.length === 0 ? (
                <Alert severity="info">
                    No load securing examples found. Try adjusting your search criteria.
                </Alert>
            ) : (
                <Grid container spacing={3}>
                    {images.map((image) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={image._id}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    cursor: 'pointer',
                                    '&:hover': { 
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                                    }
                                }}
                                onClick={() => handleImageClick(image)}
                            >
                                {/* Image */}
                                <Box sx={{ position: 'relative' }}>
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={image.imageUrl || '/default-load-image.jpg'}
                                        alt={image.title}
                                        sx={{ 
                                            objectFit: 'cover',
                                            bgcolor: 'grey.200'
                                        }}
                                    />
                                    
                                    {/* Zoom Overlay */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            bgcolor: 'rgba(0,0,0,0.7)',
                                            borderRadius: '50%',
                                            p: 1.5,
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            '.MuiCard-root:hover &': {
                                                opacity: 1
                                            }
                                        }}
                                    >
                                        <ZoomIn sx={{ color: 'white' }} />
                                    </Box>

                                    {/* Security Rating Badge */}
                                    {image.tags && image.tags.length > 0 && (
                                        <Chip
                                            {...getSecurityRating(image.tags)}
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                left: 8
                                            }}
                                        />
                                    )}
                                </Box>

                                <CardContent>
                                    {/* Title */}
                                    <Typography variant="h6" gutterBottom sx={{ 
                                        fontWeight: 'bold',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical'
                                    }}>
                                        {image.title}
                                    </Typography>

                                    {/* Description */}
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        sx={{ 
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            mb: 2
                                        }}
                                    >
                                        {image.description}
                                    </Typography>

                                    {/* Customer/Truck Info */}
                                    <Stack spacing={1}>
                                        {image.relatedCustomer && (
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Business fontSize="small" color="action" />
                                                <Typography variant="caption" color="text.secondary">
                                                    {image.relatedCustomer.name}
                                                </Typography>
                                            </Stack>
                                        )}
                                        
                                        {image.relatedTruck && (
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <LocalShipping fontSize="small" color="action" />
                                                <Typography variant="caption" color="text.secondary">
                                                    {image.relatedTruck.truckNumber} - {image.relatedTruck.make} {image.relatedTruck.model}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>

                                    {/* Tags */}
                                    {image.tags && image.tags.length > 0 && (
                                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
                                            {image.tags.slice(0, 3).map((tag, index) => (
                                                <Chip 
                                                    key={index} 
                                                    label={tag} 
                                                    size="small" 
                                                    variant="outlined"
                                                />
                                            ))}
                                            {image.tags.length > 3 && (
                                                <Chip 
                                                    label={`+${image.tags.length - 3} more`} 
                                                    size="small" 
                                                    variant="outlined" 
                                                    color="secondary"
                                                />
                                            )}
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Image Detail Dialog */}
            <Dialog
                open={imageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { height: '90vh' }
                }}
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {selectedImage?.title}
                        </Typography>
                        <IconButton onClick={() => setImageDialogOpen(false)}>
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                
                <DialogContent>
                    {selectedImage && (
                        <Grid container spacing={3}>
                            {/* Large Image */}
                            <Grid item xs={12} md={8}>
                                <Box sx={{ 
                                    width: '100%',
                                    height: '60vh',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'grey.100',
                                    borderRadius: 1,
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src={selectedImage.imageUrl}
                                        alt={selectedImage.title}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </Box>
                            </Grid>
                            
                            {/* Image Details */}
                            <Grid item xs={12} md={4}>
                                <Stack spacing={3}>
                                    {/* Description */}
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                            Description
                                        </Typography>
                                        <Typography variant="body2">
                                            {selectedImage.description}
                                        </Typography>
                                        
                                        {selectedImage.imageCaption && (
                                            <>
                                                <Divider sx={{ my: 2 }} />
                                                <Typography variant="body2" fontStyle="italic">
                                                    {selectedImage.imageCaption}
                                                </Typography>
                                            </>
                                        )}
                                    </Paper>

                                    {/* Security Assessment */}
                                    {selectedImage.tags && selectedImage.tags.length > 0 && (
                                        <Paper sx={{ p: 2 }}>
                                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                                Security Assessment
                                            </Typography>
                                            <Chip
                                                {...getSecurityRating(selectedImage.tags)}
                                                sx={{ mb: 2 }}
                                            />
                                            <Stack spacing={1}>
                                                {selectedImage.tags.map((tag, index) => (
                                                    <Chip 
                                                        key={index} 
                                                        label={tag.replace(/_/g, ' ')} 
                                                        size="small" 
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Stack>
                                        </Paper>
                                    )}

                                    {/* Related Information */}
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                            Related Information
                                        </Typography>
                                        
                                        {selectedImage.relatedCustomer && (
                                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                                <Business fontSize="small" color="primary" />
                                                <Typography variant="body2">
                                                    Customer: {selectedImage.relatedCustomer.name}
                                                </Typography>
                                            </Stack>
                                        )}
                                        
                                        {selectedImage.relatedTruck && (
                                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                                <LocalShipping fontSize="small" color="primary" />
                                                <Typography variant="body2">
                                                    Truck: {selectedImage.relatedTruck.truckNumber}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {selectedImage.relatedTruck.make} {selectedImage.relatedTruck.model}
                                                </Typography>
                                            </Stack>
                                        )}

                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            Added: {new Date(selectedImage.createdAt).toLocaleDateString()}
                                        </Typography>
                                    </Paper>

                                    {/* Equipment Types */}
                                    {selectedImage.equipmentTypes && selectedImage.equipmentTypes.length > 0 && (
                                        <Paper sx={{ p: 2 }}>
                                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                                Equipment Used
                                            </Typography>
                                            <Stack spacing={1}>
                                                {selectedImage.equipmentTypes.map((equipment, index) => (
                                                    <Typography key={index} variant="body2">
                                                        • {equipment}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </Paper>
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default LoadSecuringGallery; 